import type { DigitalEventAsset, DigitalEventEdge, EbookAsset, EbookEdge, StudyAsset, StudyEdge } from "./types"

/** The graph payload behind a card, kept verbatim so logging shows what really arrived. */
export type LibraryCardSource = StudyAsset | EbookAsset | DigitalEventAsset

/**
 * The whole edge, not just the asset. `addedAt` and the `LibraryItem` wrapper are
 * where the grant time and the join keys live, and both are invisible if the log
 * only ever sees `node.asset`.
 */
export type LibraryEdgeSource = StudyEdge | EbookEdge | DigitalEventEdge

/**
 * Dev-only. This prototype exists to find out where the DCE graph differs from what
 * the Figma frame assumes, so hovering or clicking a card dumps the untouched asset
 * next to the projection the UI actually rendered. Remove once the shape is settled.
 */
export const logCardInteraction = (
  interaction: "hover" | "click",
  section: string,
  card: { readonly id: string; readonly title: string; readonly href: string },
  source: LibraryCardSource,
): void => {
  if (!import.meta.env.DEV) {
    return
  }

  console.groupCollapsed(`[library:${section}] ${interaction} — ${source.__typename} ${card.id}`)
  console.log("asset (raw from graph):", source)
  console.log("card (projected):", card)
  console.log(
    "fields present:",
    Object.entries(source).filter(([, value]) => value !== null && value !== undefined),
  )
  console.log(
    "fields null/absent:",
    Object.entries(source)
      .filter(([, value]) => value === null || value === undefined)
      .map(([key]) => key),
  )

  console.groupEnd()
}

/** Scalar-ish fields flatten into one table row; nested objects get their own column value. */
const flatten = (asset: LibraryCardSource): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(asset).flatMap(([key, value]) =>
      value !== null && typeof value === "object" && !Array.isArray(value)
        ? Object.entries(value as Record<string, unknown>).map(([nested, inner]) => [`${key}.${nested}`, inner])
        : [[key, value]],
    ),
  )

/** The edge's own fields, prefixed so they cannot collide with the asset's. */
const flattenEdge = (edge: LibraryEdgeSource): Record<string, unknown> => ({
  addedAt: edge.addedAt,
  cursor: edge.cursor,
  "item.id": edge.node.id,
  ...flatten(edge.node.asset as LibraryCardSource),
})

/**
 * Dev-only. Dumps every edge a list received, so the shape the graph actually
 * serves can be read against real UAT data instead of fixtures. Remove with the
 * rest of this prototype's telemetry once the shape is settled.
 *
 * Logged as one group per section with the count in the header, so three
 * sections loading at once stay legible — and an EMPTY section still logs, since
 * "the graph returned nothing here" is the finding at least as often as the rows
 * are (FINDINGS.md gap 9).
 */
export const logLibraryEdges = (section: string, edges: readonly LibraryEdgeSource[]): void => {
  if (!import.meta.env.DEV) {
    return
  }

  console.groupCollapsed(`[library:${section}] ${edges.length} edge(s) from the graph`)

  if (edges.length > 0) {
    console.table(edges.map(flattenEdge))
    console.log(
      "assets:",
      edges.map(edge => edge.node.asset),
    )
  }

  console.log("raw edges:", edges)
  console.groupEnd()
}
